import { getSyncStatus } from '../data/sync';
import type { LatLng } from '../logic/route';
import { h } from './dom';
import { createMap, pinIcon } from './maps';

/** A small map of one business, where its row says it is (D80). Lazy like the stops map: Maps JS loads
 *  after the card is on screen; offline, or when Maps fails, it simply stays hidden. */
export const placeMap = (): {
  el: HTMLElement;
  show: (at: LatLng, label: string) => Promise<void>;
  hide: () => void;
  dispose: () => void;
} => {
  const el = h('div', { class: 'placemap', hidden: true, role: 'img', 'aria-label': 'Map of the business' });
  let mapPromise: Promise<google.maps.Map> | null = null;
  let marker: { setMap: (m: google.maps.Map | null) => void } | null = null;
  let seq = 0;

  const show = async (at: LatLng, label: string): Promise<void> => {
    const mine = ++seq;
    // offline is what the sync engine found out, not navigator.onLine (D68)
    if (getSyncStatus().state === 'offline') {
      el.hidden = true;
      return;
    }
    try {
      mapPromise ??= createMap(el, at, 15).catch((err: unknown) => {
        mapPromise = null; // a failed load may be retried on the next show()
        throw err;
      });
      const map = await mapPromise;
      if (mine !== seq) return;
      const { Marker } = await google.maps.importLibrary('marker');
      marker?.setMap(null);
      marker = new Marker({ map, position: at, icon: pinIcon('prospect', true), title: label });
      map.setCenter(at);
      el.hidden = false;
    } catch {
      el.hidden = true;
    }
  };

  const hide = () => {
    seq++;
    el.hidden = true;
  };

  return {
    el,
    show,
    hide,
    dispose: () => {
      seq++;
      marker?.setMap(null);
      marker = null;
    },
  };
};
